import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Button, Skeleton } from "@mui/material";
import { ArrowBack, Print } from "@mui/icons-material";
import { useGetBranchTallyDetailQuery } from "store/services/branchTallyApi";

const fmt = (d) =>
  d
    ? new Date(d).toLocaleString("en-NP", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
const initials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const PRINT_STYLE = `
  @media print {
    body * { visibility: hidden !important; }
    #tally-print-area, #tally-print-area * { visibility: visible !important; }
    #tally-print-area { position: fixed; inset: 0; padding: 0; margin: 0; }
    .no-print { display: none !important; }
    @page { size: A4; margin: 12mm 14mm; }
  }
`;

const Row = ({ label, value, mono = false }) => (
  <tr>
    <td
      style={{
        padding: "5px 10px 5px 0",
        fontSize: 11,
        color: "#64748b",
        fontWeight: 600,
        whiteSpace: "nowrap",
        verticalAlign: "top",
        width: 120,
      }}
    >
      {label}
    </td>
    <td
      style={{
        padding: "5px 0",
        fontSize: 11,
        fontWeight: 700,
        color: "#0f172a",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value ?? "—"}
    </td>
  </tr>
);

const SectionHeader = ({ title, count, borderColor = "#0f172a" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottom: `1px solid ${borderColor}`,
      paddingBottom: 6,
      marginBottom: 10,
      marginTop: 20,
    }}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: 1,
        color: borderColor,
      }}
    >
      {title}
    </span>
    <span
      style={{
        fontSize: 11,
        fontWeight: 800,
        fontFamily: "monospace",
        border: `1.5px solid ${borderColor}`,
        color: borderColor,
        borderRadius: 4,
        padding: "0px 8px",
      }}
    >
      {count}
    </span>
  </div>
);

const ShipmentChip = ({ id, color }) => (
  <span
    style={{
      display: "inline-block",
      fontFamily: "monospace",
      fontWeight: 700,
      fontSize: 10,
      color,
      background: `${color}12`,
      border: `1px solid ${color}33`,
      borderRadius: 5,
      padding: "2px 7px",
      letterSpacing: 0.3,
      lineHeight: 1.9,
      marginBottom: 3,
    }}
  >
    {id}
  </span>
);

const ScannerGroup = ({ scannerName, shipments, color }) => (
  <div
    style={{
      border: `1px solid ${color}25`,
      borderRadius: 8,
      overflow: "hidden",
      marginBottom: 10,
      breakInside: "avoid",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "7px 14px",
        background: `${color}0d`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#1e3a5f,#2563eb)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          {initials(scannerName || "?")}
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 12, color: "#0f172a" }}>
            {scannerName || "Unknown"}
          </div>
          <div style={{ fontSize: 10, color: "#64748b" }}>
            {shipments.length} shipment{shipments.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>
      <span
        style={{
          background: color,
          color: "#fff",
          borderRadius: 20,
          padding: "1px 10px",
          fontSize: 11,
          fontWeight: 800,
          fontFamily: "monospace",
        }}
      >
        {shipments.length}
      </span>
    </div>
    <div
      style={{
        padding: "8px 14px",
        background: "#fff",
        display: "flex",
        flexWrap: "wrap",
        gap: 5,
      }}
    >
      {shipments.map((id, i) => (
        <ShipmentChip key={i} id={id} color={color} />
      ))}
    </div>
  </div>
);

const GroupedTallied = ({ rows = [] }) => {
  if (rows.length === 0)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 12,
          padding: "12px 0",
        }}
      >
        No tallied shipments.
      </div>
    );
  const groups = rows.reduce((acc, r) => {
    const key = r.scannedBy || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(r.shipmentId);
    return acc;
  }, {});
  return (
    <>
      {Object.entries(groups).map(([name, shipments]) => (
        <ScannerGroup
          key={name}
          scannerName={name}
          shipments={shipments}
          color="#059669"
        />
      ))}
    </>
  );
};

const GroupedMissingSystem = ({ rows = [] }) => {
  if (rows.length === 0)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 12,
          padding: "12px 0",
        }}
      >
        No unrecognised shipments scanned.
      </div>
    );
  const groups = rows.reduce((acc, r) => {
    const key = r.scannedBy || "Unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(r.scannedShipmentId);
    return acc;
  }, {});
  return (
    <>
      {Object.entries(groups).map(([name, shipments]) => (
        <ScannerGroup
          key={name}
          scannerName={name}
          shipments={shipments}
          color="#9333ea"
        />
      ))}
    </>
  );
};

const MissingPhysicalSection = ({ rows = [] }) => {
  if (rows.length === 0)
    return (
      <div
        style={{
          textAlign: "center",
          color: "#94a3b8",
          fontSize: 12,
          padding: "12px 0",
        }}
      >
        All expected shipments found physically.
      </div>
    );
  return (
    <div
      style={{
        border: "1px solid #fee2e2",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "7px 14px",
          background: "#fff5f5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 12, color: "#dc2626" }}>
          Not found physically
        </span>
        <span
          style={{
            background: "#dc2626",
            color: "#fff",
            borderRadius: 20,
            padding: "1px 10px",
            fontSize: 11,
            fontWeight: 800,
            fontFamily: "monospace",
          }}
        >
          {rows.length}
        </span>
      </div>
      <div
        style={{
          padding: "8px 14px",
          background: "#fff",
          display: "flex",
          flexWrap: "wrap",
          gap: 5,
        }}
      >
        {rows.map((r, i) => (
          <ShipmentChip key={i} id={r.shipmentId || r} color="#dc2626" />
        ))}
      </div>
    </div>
  );
};

const PrintArea = ({ tally }) => {
  const talliedOrders = tally.talliedOrders ?? [];
  const missingPhysical = tally.missingPhysical ?? [];
  const missingSystem = tally.missingSystem ?? [];

  return (
    <div
      id="tally-print-area"
      style={{
        background: "#fff",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        color: "#0f172a",
        maxWidth: 860,
        margin: "0 auto",
        padding: "28px 36px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          paddingBottom: 14,
          marginBottom: 16,
          borderBottom: "1px double #0f172a",
        }}
      >
        <div>
          {tally.entityLogo && (
            <img
              src={tally.entityLogo}
              alt={tally.entityName}
              style={{
                height: 36,
                width: "auto",
                objectFit: "contain",
                marginBottom: 6,
                display: "block",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: -0.5,
            }}
          >
            {tally.entityName ?? "—"}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              display: "inline-block",
              color: tally.status === "Completed" ? "#059669" : "#d97706",
              padding: "8px",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 0.5,
              marginBottom: 6,
              textTransform: "uppercase",
            }}
          >
            {tally.status === "Completed" ? "Completed" : "In Progress"}
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
            {tally.branch?.name}
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          border: "1.5px solid #e2e8f0",
          borderRadius: 6,
          overflow: "hidden",
          marginBottom: 16,
        }}
      >
        {[
          { label: "Total Scanned", value: tally.totalScanned ?? 0 },
          { label: "Tallied", value: tally.talliedCount ?? 0 },
          { label: "Missing Physical", value: tally.missingPhysicalCount ?? 0 },
          { label: "Missing System", value: tally.missingSystemCount ?? 0 },
        ].map((s, i, arr) => (
          <div
            key={s.label}
            style={{
              padding: "10px 12px",
              textAlign: "center",
              borderRight: i < arr.length - 1 ? "1.5px solid #e2e8f0" : "none",
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                fontFamily: "monospace",
                color: "#0f172a",
                lineHeight: 1,
              }}
            >
              {s.value}
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                marginTop: 4,
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
          border: "1.5px solid #e2e8f0",
          borderRadius: 6,
          padding: "12px 16px",
          marginBottom: 6,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 900,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 6,
            }}
          >
            Tally Details
          </div>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <tbody>
              <Row label="Branch" value={tally.branch?.name} />
              <Row label="Initiated By" value={tally.initiatedBy?.name} />
              <Row label="Started At" value={fmt(tally.startedAt)} />
              <Row label="Completed At" value={fmt(tally.completedAt)} />
            </tbody>
          </table>
        </div>
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 900,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: 1.2,
              marginBottom: 6,
            }}
          >
            Personnel Info
          </div>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <tbody>
              <Row label="Approved By" value={tally.approvedBy?.name} />
              <Row
                label="Participants"
                value={
                  tally.participants?.length > 0
                    ? tally.participants
                        .map(
                          (p) => `${p.name}${p.isCreator ? " (Creator)" : ""}`,
                        )
                        .join(", ")
                    : "—"
                }
              />
              <Row
                label="Scanners"
                value={
                  tally.scanners?.length > 0
                    ? tally.scanners.map((s) => s.name).join(", ")
                    : "—"
                }
              />
            </tbody>
          </table>
        </div>
      </div>
      <SectionHeader
        title="Tallied Shipments"
        count={talliedOrders.length}
        borderColor="#059669"
      />
      <GroupedTallied rows={talliedOrders} />
      <SectionHeader
        title="Missing Physical"
        count={missingPhysical.length}
        borderColor="#dc2626"
      />
      <MissingPhysicalSection rows={missingPhysical} />
      <SectionHeader
        title="Missing System"
        count={missingSystem.length}
        borderColor="#9333ea"
      />
      <GroupedMissingSystem rows={missingSystem} />
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 30 }}
      >
        {[
          {
            label: "Scanned By",
            name: tally.scanners?.map((s) => s.name).join(", "),
          },
          { label: "Approved By", name: tally.approvedBy?.name },
          { label: "Signed By", name: tally.signedBy?.name },
        ].map((sig) => (
          <div key={sig.label} style={{ textAlign: "center" }}>
            <div
              style={{
                height: 48,
                borderBottom: "1.5px solid #0f172a",
                marginBottom: 6,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingBottom: 4,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
                {sig.name || ""}
              </span>
            </div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              {sig.label}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          marginTop: 16,
          paddingTop: 8,
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "end",
          fontSize: 8,
          color: "#94a3b8",
        }}
      >
        <span>Printed: {fmt(new Date())}</span>
      </div>
    </div>
  );
};

const BranchTallyPrintScreen = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { data: tally, isLoading } = useGetBranchTallyDetailQuery(id, {
    skip: !id,
  });
  const record = tally || state?.record;

  const handlePrint = () => {
    const styleId = "tally-print-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = PRINT_STYLE;
      document.head.appendChild(style);
    }
    window.print();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#e2e8f0",
        padding: "24px 20px",
      }}
    >
      <div
        className="no-print"
        style={{
          maxWidth: 860,
          margin: "0 auto 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          size="small"
          onClick={() => navigate(-1)}
          sx={{ background: "#fff" }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={!record}
          sx={{ fontWeight: 700 }}
        >
          Print / Save PDF
        </Button>
      </div>
      <div
        style={{
          maxWidth: 860,
          margin: "0 auto",
          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        {isLoading || !record ? (
          <div style={{ background: "#fff", padding: 40 }}>
            {[180, 80, 260, 200].map((h, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={h}
                style={{ marginBottom: 16 }}
              />
            ))}
          </div>
        ) : (
          <PrintArea tally={record} />
        )}
      </div>
    </div>
  );
};

export default BranchTallyPrintScreen;
